import { CwjWorkspaceError } from "./types.js";
const MUTABLE_TASK_STATUSES = new Set(["pending", "running", "paused", "approval_required"]);
const MUTABLE_WORKFLOW_STATUSES = new Set(["pending", "running", "paused"]);
function isMutableTaskStatus(status) {
    return MUTABLE_TASK_STATUSES.has(status);
}
function isMutableWorkflowStatus(status) {
    return MUTABLE_WORKFLOW_STATUSES.has(status);
}
function emergencyScopes(context) {
    const scopes = ["global"];
    if (context.tenantId)
        scopes.push(`tenant:${context.tenantId}`);
    scopes.push(`project:${context.projectId}`);
    scopes.push(`skill:${context.skillId}`);
    return scopes;
}
function contextMatch(a, b) {
    return (a.projectId === b.projectId &&
        a.skillId === b.skillId &&
        a.tenantId === b.tenantId &&
        a.ownerId === b.ownerId);
}
export class MemoryCwjWorkspaceMutationCoordinator {
    constructor(taskEngine, workflowEngine, store) {
        this.taskEngine = taskEngine;
        this.workflowEngine = workflowEngine;
        this.store = store;
    }
    async mutateTaskAndWorkflow(input, expectedTaskVersion, expectedWorkflowVersion) {
        return this.store.runExclusive(() => this.mutate(input, expectedTaskVersion, expectedWorkflowVersion));
    }
    actorId(actor) {
        return actor.actorId;
    }
    async requireTask(taskId) {
        const task = await this.taskEngine.getTask(taskId);
        if (!task) {
            throw new CwjWorkspaceError(`not_found: task ${taskId}`, "not_found");
        }
        return task;
    }
    async requireWorkflow(workflowId) {
        const workflow = await this.workflowEngine.getWorkflow(workflowId);
        if (!workflow) {
            throw new CwjWorkspaceError(`not_found: workflow ${workflowId}`, "not_found");
        }
        return workflow;
    }
    assertNotEmergencyStopped(context) {
        for (const scope of emergencyScopes(context)) {
            if (this.taskEngine.isEmergencyStopped(scope) || this.workflowEngine.isEmergencyStopped(scope)) {
                throw new CwjWorkspaceError(`emergency_stop: Emergency Stop is active for scope ${scope}`, "emergency_stop");
            }
        }
    }
    async mutate(input, expectedTaskVersion, expectedWorkflowVersion) {
        switch (input.type) {
            case "create":
                return this.create(input, expectedTaskVersion, expectedWorkflowVersion);
            case "pause":
                return this.pause(input, expectedTaskVersion, expectedWorkflowVersion);
            case "resume":
                return this.resume(input, expectedTaskVersion, expectedWorkflowVersion);
            case "cancel":
                return this.cancel(input, expectedTaskVersion, expectedWorkflowVersion);
            case "update":
                return this.update(input, expectedTaskVersion, expectedWorkflowVersion);
            case "approve":
                return this.approve(input, expectedTaskVersion, expectedWorkflowVersion);
            case "evidence":
                return this.evidence(input, expectedTaskVersion, expectedWorkflowVersion);
            default:
                throw new CwjWorkspaceError(`unknown_mutation: ${input.type}`, "unknown_mutation");
        }
    }
    async create(input, expectedTaskVersion, expectedWorkflowVersion) {
        if (!input.taskContext || !input.taskInput || !input.workflowContext || !input.workflowInput) {
            throw new CwjWorkspaceError("invalid_input: create requires task and workflow context/input", "invalid_input");
        }
        if (expectedTaskVersion !== 0 || expectedWorkflowVersion !== 0) {
            throw new CwjWorkspaceError("concurrency: create expects version 0", "concurrency");
        }
        const idempotencyKey = input.taskInput.idempotencyKey;
        if (idempotencyKey) {
            const record = this.store.getIdempotencyRecord(input.taskContext, idempotencyKey);
            if (record) {
                const existingTask = this.store.findTaskByIdempotencyKey(input.taskContext, idempotencyKey);
                const existingWorkflow = this.store.findWorkflowByIdempotencyKey(input.workflowContext, idempotencyKey);
                if (!existingTask || !existingWorkflow) {
                    throw new CwjWorkspaceError("idempotency_incomplete: idempotency record is missing one side", "idempotency_incomplete");
                }
                this.assertIdempotencyMatch(input, existingTask, existingWorkflow, record, idempotencyKey);
                return { task: existingTask, workflow: existingWorkflow };
            }
        }
        this.assertNotEmergencyStopped(input.taskContext);
        const task = await this.taskEngine.createTask(input.taskContext, input.taskInput, this.actorId(input.actor));
        const workflowInput = {
            ...input.workflowInput,
            taskId: task.taskId,
        };
        const workflow = await this.workflowEngine.createWorkflow(input.workflowContext, workflowInput, this.actorId(input.actor));
        const started = await this.workflowEngine.startWorkflow(workflow.workflowId, this.actorId(input.actor));
        if (idempotencyKey) {
            this.store.registerIdempotency(input.taskContext, idempotencyKey, {
                taskId: task.taskId,
                workflowId: started.workflowId,
                taskInputHash: this.store.hashTaskInput(input.taskInput),
                workflowInputHash: this.store.hashWorkflowInput(input.workflowInput),
            });
        }
        return { task, workflow: started };
    }
    assertIdempotencyMatch(input, existingTask, existingWorkflow, record, idempotencyKey) {
        if (!contextMatch(existingTask.context, input.taskContext) ||
            !contextMatch(existingWorkflow.context, input.workflowContext)) {
            throw new CwjWorkspaceError(`idempotency_conflict: idempotency key ${idempotencyKey} used with different context`, "idempotency_conflict");
        }
        if (record.taskInputHash !== this.store.hashTaskInput(input.taskInput) ||
            record.workflowInputHash !== this.store.hashWorkflowInput(input.workflowInput)) {
            throw new CwjWorkspaceError(`idempotency_conflict: idempotency key ${idempotencyKey} used with different payload`, "idempotency_conflict");
        }
    }
    async pause(input, expectedTaskVersion, expectedWorkflowVersion) {
        return this.twoPhaseMutation(input, expectedTaskVersion, expectedWorkflowVersion, (taskId, actorId) => this.taskEngine.pauseTask(taskId, actorId), (workflowId, actorId) => this.workflowEngine.pauseWorkflow(workflowId, actorId));
    }
    async resume(input, expectedTaskVersion, expectedWorkflowVersion) {
        return this.twoPhaseMutation(input, expectedTaskVersion, expectedWorkflowVersion, (taskId, actorId) => this.taskEngine.resumeTask(taskId, actorId), (workflowId, actorId) => this.workflowEngine.resumeWorkflow(workflowId, actorId));
    }
    async cancel(input, expectedTaskVersion, expectedWorkflowVersion) {
        return this.twoPhaseMutation(input, expectedTaskVersion, expectedWorkflowVersion, (taskId, actorId) => this.taskEngine.cancelTask(taskId, actorId), (workflowId, actorId) => this.workflowEngine.cancelWorkflow(workflowId, actorId));
    }
    async twoPhaseMutation(input, expectedTaskVersion, expectedWorkflowVersion, mutateTask, mutateWorkflow) {
        const taskId = input.taskId;
        if (!taskId) {
            throw new CwjWorkspaceError("invalid_input: missing taskId", "invalid_input");
        }
        const task = await this.requireTask(taskId);
        const workflow = await this.requireWorkflow(taskId);
        if (!contextMatch(task.context, workflow.context)) {
            throw new CwjWorkspaceError(`task_drift: task and workflow context do not match for ${taskId}`, "task_drift");
        }
        if (task.version !== expectedTaskVersion) {
            throw new CwjWorkspaceError(`concurrency: expected task version ${expectedTaskVersion}, found ${task.version}`, "concurrency");
        }
        if (workflow.version !== expectedWorkflowVersion) {
            throw new CwjWorkspaceError(`concurrency: expected workflow version ${expectedWorkflowVersion}, found ${workflow.version}`, "concurrency");
        }
        this.assertNotEmergencyStopped(task.context);
        if (!isMutableTaskStatus(task.status)) {
            throw new CwjWorkspaceError(`invalid_transition: task ${taskId} is in terminal state ${task.status}`, "invalid_transition");
        }
        if (!isMutableWorkflowStatus(workflow.status)) {
            throw new CwjWorkspaceError(`invalid_transition: workflow ${taskId} is in terminal state ${workflow.status}`, "invalid_transition");
        }
        const nextTask = await mutateTask(taskId, this.actorId(input.actor));
        const nextWorkflow = await mutateWorkflow(taskId, this.actorId(input.actor));
        return { task: nextTask, workflow: nextWorkflow };
    }
    async update(input, expectedTaskVersion, expectedWorkflowVersion) {
        const taskId = input.taskId;
        if (!taskId || input.updates === undefined) {
            throw new CwjWorkspaceError("invalid_input: update requires taskId and updates", "invalid_input");
        }
        const task = await this.requireTask(taskId);
        const workflow = await this.workflowEngine.getWorkflow(taskId);
        if (task.version !== expectedTaskVersion) {
            throw new CwjWorkspaceError(`concurrency: expected task version ${expectedTaskVersion}, found ${task.version}`, "concurrency");
        }
        if (workflow) {
            if (workflow.version !== expectedWorkflowVersion) {
                throw new CwjWorkspaceError(`concurrency: expected workflow version ${expectedWorkflowVersion}, found ${workflow.version}`, "concurrency");
            }
            if (!contextMatch(task.context, workflow.context)) {
                throw new CwjWorkspaceError(`task_drift: task and workflow context do not match for ${taskId}`, "task_drift");
            }
        }
        this.assertNotEmergencyStopped(task.context);
        if (!isMutableTaskStatus(task.status)) {
            throw new CwjWorkspaceError(`invalid_transition: task ${taskId} cannot be updated in state ${task.status}`, "invalid_transition");
        }
        const nextTask = await this.taskEngine.updateTask(taskId, input.updates, expectedTaskVersion, this.actorId(input.actor));
        return { task: nextTask, workflow };
    }
    async approve(input, expectedTaskVersion, expectedWorkflowVersion) {
        const taskId = input.taskId;
        if (!taskId) {
            throw new CwjWorkspaceError("invalid_input: missing taskId", "invalid_input");
        }
        const task = await this.requireTask(taskId);
        const workflow = await this.requireWorkflow(taskId);
        this.validateTaskWorkflowPair(task, workflow, expectedTaskVersion, expectedWorkflowVersion);
        const nextWorkflow = await this.workflowEngine.approveStage(taskId, workflow.currentStageId, this.actorId(input.actor));
        const refreshedTask = await this.taskEngine.getTask(taskId);
        return { task: refreshedTask, workflow: nextWorkflow };
    }
    async evidence(input, expectedTaskVersion, expectedWorkflowVersion) {
        const taskId = input.taskId;
        if (!taskId || input.evidenceType === undefined || input.payload === undefined) {
            throw new CwjWorkspaceError("invalid_input: evidence requires taskId, evidenceType and payload", "invalid_input");
        }
        const task = await this.requireTask(taskId);
        const workflow = await this.requireWorkflow(taskId);
        this.validateTaskWorkflowPair(task, workflow, expectedTaskVersion, expectedWorkflowVersion);
        const nextWorkflow = await this.workflowEngine.submitStageEvidence(taskId, workflow.currentStageId, input.evidenceType, input.payload, this.actorId(input.actor));
        const refreshedTask = await this.taskEngine.getTask(taskId);
        return { task: refreshedTask, workflow: nextWorkflow };
    }
    validateTaskWorkflowPair(task, workflow, expectedTaskVersion, expectedWorkflowVersion) {
        if (!contextMatch(task.context, workflow.context)) {
            throw new CwjWorkspaceError(`task_drift: task and workflow context do not match for ${task.taskId}`, "task_drift");
        }
        if (task.version !== expectedTaskVersion) {
            throw new CwjWorkspaceError(`concurrency: expected task version ${expectedTaskVersion}, found ${task.version}`, "concurrency");
        }
        if (workflow.version !== expectedWorkflowVersion) {
            throw new CwjWorkspaceError(`concurrency: expected workflow version ${expectedWorkflowVersion}, found ${workflow.version}`, "concurrency");
        }
        if (!isMutableTaskStatus(task.status)) {
            throw new CwjWorkspaceError(`invalid_transition: task ${task.taskId} is in terminal state ${task.status}`, "invalid_transition");
        }
        if (!isMutableWorkflowStatus(workflow.status)) {
            throw new CwjWorkspaceError(`invalid_transition: workflow ${task.taskId} is in terminal state ${workflow.status}`, "invalid_transition");
        }
    }
}
//# sourceMappingURL=mutationCoordinator.js.map